// src/oura/newscan/ScanFlowContext.ts
//
// The New scan flow's shared state, read by every step through useScanFlow().
//
// The page owns it (useScanFlowState) and provides it with
// <ScanFlowContext.Provider>. It holds what lives in the URL (kind, step,
// draft id) and the draft controller (useScanDraft), and composes the two
// pre-draft pieces: the website read and Business form (useSiteRead) and the
// Search Console state (useGscState). Once a draft exists the server holds
// the work: closing the page never loses it.
//
// The foot lives in a small store outside React state, so a step that
// declares its foot on every render re-renders only the foot, never itself.
// Each step's `foot.set` is bound to that step, and the foot shows only the
// current step's spec, so a step that is leaving cannot leave stale actions.
//
// No components here (a .ts file): the provider is the context's own
// `.Provider`, rendered by the page.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { ScanDraftBusiness, ScanDraftFailure, ScanDraftKind, ScanDraftPurpose } from "@service/aeo/scanDraftTypes";
import { createScanDraft } from "@service/aeo/scanDraft";
import { useUserProfileContext } from "@context/user_profile";
import { OURA_ROOT } from "@oura/nav";
import { scanDraftMode } from "./flags";
import { useScanDraft, type ScanDraftController } from "./useScanDraft";
import { FIRST_CHECK, FRAME } from "./copy";
import { onboardingHref } from "./firstCheckLinks";
import { canReachStep, defaultStep, isStepKey, stepsFor, type FootSpec, type StepKey, type StepReach } from "./flowSteps";
import { clearPreDraft, useSiteRead, type ScanFlowSite } from "./useSiteRead";
import { useGscState, type ScanFlowGsc } from "./useGscState";
import { useBusinessSeen } from "./businessSeen";

export type { ScanFlowSite } from "./useSiteRead";
export type { GscStatus, ScanFlowGsc } from "./useGscState";

/* ------------------------------------------------------------------------- *
 * Public shape
 * ------------------------------------------------------------------------- */

export interface ScanFlowValue {
    kind: ScanDraftKind;
    /**
     * `first_check`: a signed client's First AI check (JOURNEY-SPEC §2.5).
     * Null: the kind's own purpose. From the draft once loaded, else the URL.
     */
    purpose: ScanDraftPurpose | null;
    businessId: string | null;
    steps: StepKey[];
    step: StepKey;
    goTo(step: StepKey): void;
    canReach(step: StepKey): boolean;
    /** First AI check: staff pressed "Looks right" on Business for this draft. Always true on other purposes. */
    businessSeen: boolean;
    /** Records "Looks right" for this draft (browser only, businessSeen.ts). */
    markBusinessSeen(): void;
    /** Leaves the flow. `notice` replaces the "Draft saved" line on the screen it lands on. */
    close(opts?: { notice?: string }): void;
    ctl: ScanDraftController;
    /** The draft id in the URL, or null before one exists. */
    draftId: string | null;

    // new_scan, before a draft exists
    site: ScanFlowSite;
    setDomain(d: string): void;
    readSite(): Promise<void>;
    form: ScanDraftBusiness | null;
    setForm(next: ScanDraftBusiness): void;
    gsc: ScanFlowGsc;
    connectGsc(): Promise<void>;
    recheckGsc(): Promise<void>;
    /** Posts the form, puts ?draft= in the URL and goes to Review. Null on success. */
    createDraft(): Promise<ScanDraftFailure | null>;
    /** createDraft is running. */
    creating: boolean;
    /** Opens an existing draft (Website step: "Continue it"). */
    openDraft(draftId: string): void;
    /**
     * An existing business only: discards this draft and opens a fresh one of
     * the same kind. A failed recipe update starts over this way, because a
     * recipe update cannot be drafted again in place. Null on success.
     */
    startOver(): Promise<ScanDraftFailure | null>;

    /** Each step declares its foot here, in an effect. `null` clears it. */
    foot: { set(next: FootSpec | null): void };
}

export const ScanFlowContext = createContext<ScanFlowValue | null>(null);

/** The flow, for a step or a shared piece rendered inside the New scan page. */
export function useScanFlow(): ScanFlowValue {
    const value = useContext(ScanFlowContext);
    if (!value) throw new Error("useScanFlow must be used inside the New scan page");
    return value;
}

/* ------------------------------------------------------------------------- *
 * The foot store
 * ------------------------------------------------------------------------- */

interface FootState {
    step: StepKey | null;
    spec: FootSpec | null;
}

export interface FootStore {
    get(): FootState;
    set(step: StepKey, spec: FootSpec | null): void;
    subscribe(fn: () => void): () => void;
}

function createFootStore(): FootStore {
    let state: FootState = { step: null, spec: null };
    const subs = new Set<() => void>();
    return {
        get: () => state,
        set(step, spec) {
            state = { step, spec };
            subs.forEach((fn) => fn());
        },
        subscribe(fn) {
            subs.add(fn);
            return () => {
                subs.delete(fn);
            };
        },
    };
}

/** The current step's foot, or null while the step has not declared one. */
export function useFootSpec(store: FootStore, step: StepKey): FootSpec | null {
    const state = useSyncExternalStore(store.subscribe, store.get, store.get);
    return state.step === step ? state.spec : null;
}

/** Router state an entry link opens the flow with, so Close goes back where staff came from. */
export interface ScanFlowOriginState {
    from: string;
}

/** The screen the flow was opened from: an Oura path outside the flow itself, else null. */
function originOf(state: unknown): string | null {
    if (!state || typeof state !== "object") return null;
    const from = (state as { from?: unknown }).from;
    if (typeof from !== "string" || !from.startsWith(`${OURA_ROOT}/`)) return null;
    const path = from.split(/[?#]/)[0];
    if (path === `${OURA_ROOT}/businesses/new` || path.endsWith("/questions/draft")) return null;
    return from;
}

/** Draft statuses in which a draft still holds work (Close says "Draft saved"). */
const OPEN_STATUSES = ["building", "ready", "approved", "failed"];

/* ------------------------------------------------------------------------- *
 * The state the page provides
 * ------------------------------------------------------------------------- */

export interface ScanFlowSetup {
    /** From the route: the new-business URL, or the business's draft URL. */
    routeKind: "new" | "business";
    businessId: string | null;
}

export function useScanFlowState({ routeKind, businessId }: ScanFlowSetup): {
    value: ScanFlowValue;
    footStore: FootStore;
    /** The page cannot settle on a step yet (the draft is loading or being opened). */
    pending: boolean;
    /** A failure opening the draft for an existing business. */
    openError: ScanDraftFailure | null;
    retryOpen(): void;
    /** A calm line for the page, e.g. a draft of the other kind was opened instead. */
    notice: string | null;
} {
    const navigate = useNavigate();
    const location = useLocation();
    // Read once: the flow's own URL moves replace the history entry without it.
    const [origin] = useState<string | null>(() => originOf(location.state));
    const [params, setParams] = useSearchParams();
    const draftId = params.get("draft");
    const askedStep = params.get("step");
    const askedKind = params.get("kind");
    const askedPurpose: ScanDraftPurpose | null = params.get("purpose") === "first_check" ? "first_check" : null;

    const ctl = useScanDraft(draftId);
    const draft = ctl.draft;

    // A first_check opens on a business, as a new_scan draft with that purpose (H2-a).
    const routeFirstCheck = routeKind === "business" && askedPurpose === "first_check";
    const routeDraftKind: ScanDraftKind =
        routeKind === "new" || routeFirstCheck ? "new_scan" : askedKind === "recipe_change" ? "recipe_change" : "edit_set";
    const kind: ScanDraftKind = draft?.kind ?? routeDraftKind;
    const purpose: ScanDraftPurpose | null = draft ? draft.purpose ?? null : routeFirstCheck ? "first_check" : null;
    const firstCheck = purpose === "first_check";
    const effectiveBusinessId = draft?.businessId ?? businessId;
    const steps = stepsFor(kind, purpose);

    /* ---- before a draft: the read and the form, then Search Console ---- */
    // The kept pre-draft state belongs to this user and workspace, not to the tab.
    const [, userInfo] = useUserProfileContext();
    const storageScope = userInfo?.id ? `${userInfo.id}:${userInfo.company_id ?? ""}` : "";
    const { site, setDomain, readSite, form, setForm } = useSiteRead({ routeKind, draftId, draft, storageScope });
    const { gsc, connectGsc, recheckGsc } = useGscState({
        // Connect works as soon as a site is typed; the automatic check waits for
        // the read (or the draft), so typing does not call it on every key.
        gscDomain: draft?.domain ?? site.normalized,
        settledDomain: draft?.domain ?? (site.detected ? site.normalized : ""),
        hasDraft: draft !== null,
        refreshSearch: ctl.refreshSearch,
    });
    const [creating, setCreating] = useState(false);

    /* ---- URL moves ---- */
    const openDraft = useCallback(
        (id: string) => {
            clearPreDraft(storageScope);
            // Replace: Back from Review leaves the flow, as Close does, instead of
            // landing on a detached Business form for a draft that already exists.
            setParams(
                (was) => {
                    const next = new URLSearchParams(was);
                    next.set("draft", id);
                    // The step follows the draft: Review, or Start once it is approved.
                    next.delete("step");
                    return next;
                },
                { replace: true },
            );
        },
        [setParams, storageScope],
    );

    const normalized = site.normalized;
    const createDraft = useCallback(async (): Promise<ScanDraftFailure | null> => {
        if (!form || !normalized) {
            return { ok: false, kind: "refused", message: FRAME.readFirst };
        }
        setCreating(true);
        const res = await createScanDraft({ kind: "new_scan", domain: normalized, inputs: form });
        setCreating(false);
        if (!res.ok) return res;
        openDraft(res.data.draft.id);
        return null;
    }, [form, normalized, openDraft]);

    /* ---- opening a draft for an existing business ---- */
    const [openError, setOpenError] = useState<ScanDraftFailure | null>(null);
    const [openTry, setOpenTry] = useState(0);
    // The kind asked for when the server answered "a draft is already open":
    // checked against that draft's kind once it loads.
    const existsFor = useRef<ScanDraftKind | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const mode = scanDraftMode();
    useEffect(() => {
        if (routeKind !== "business" || draftId || !businessId || mode === "absent") return;
        // A route change cancels the request: its answer belongs to the business it was asked for.
        let cancelled = false;
        setOpenError(null);
        const req = routeFirstCheck
            ? ({ kind: "new_scan", purpose: "first_check", businessId } as const)
            : ({ kind: routeDraftKind === "recipe_change" ? "recipe_change" : "edit_set", businessId } as const);
        void createScanDraft(req).then((res) => {
            if (cancelled) return;
            const id = res.ok ? res.data.draft.id : res.kind === "exists" ? res.data?.draftId ?? null : null;
            if (id) {
                // A first_check reuses the site's open draft by design (H2-a): no "other kind" line.
                existsFor.current = res.ok || routeFirstCheck ? null : routeDraftKind;
                setParams(
                    (was) => {
                        const next = new URLSearchParams(was);
                        next.set("draft", id);
                        return next;
                    },
                    { replace: true },
                );
                return;
            }
            if (!res.ok) setOpenError(res);
        });
        return () => {
            cancelled = true;
        };
        // One attempt per change; a retry bumps openTry.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeKind, draftId, businessId, routeDraftKind, routeFirstCheck, mode, openTry]);
    const retryOpen = useCallback(() => setOpenTry((n) => n + 1), []);

    const discardDraft = ctl.discard;
    const clearLine = ctl.clearMessage;
    const startOver = useCallback(async (): Promise<ScanDraftFailure | null> => {
        if (routeKind !== "business") return { ok: false, kind: "refused", message: FRAME.noBusiness };
        const fail = await discardDraft();
        if (fail) return fail;
        // "Draft discarded" is not news here: a fresh draft is on its way.
        clearLine();
        // Without ?draft= the opener above creates a fresh draft of this kind.
        setParams(
            (was) => {
                const next = new URLSearchParams(was);
                next.delete("draft");
                if (firstCheck) {
                    next.set("purpose", "first_check");
                    next.delete("kind");
                } else next.set("kind", kind);
                // A fresh First AI check draft opens on Business, as every First AI check does.
                next.set("step", firstCheck ? "business" : "review");
                return next;
            },
            { replace: true },
        );
        return null;
    }, [routeKind, discardDraft, clearLine, kind, firstCheck, setParams]);

    // The open draft is of the other kind (an edit while a recipe update was
    // asked for, or the reverse): it opens, ?kind= follows it, and a line says why.
    useEffect(() => {
        const asked = existsFor.current;
        if (!draft || !asked) return;
        existsFor.current = null;
        if (draft.kind === asked) return;
        setNotice(FRAME.otherKindOpen[draft.kind]);
        setParams(
            (was) => {
                const next = new URLSearchParams(was);
                next.set("kind", draft.kind);
                return next;
            },
            { replace: true },
        );
    }, [draft, setParams]);

    /* ---- steps ---- */
    const [seenStored, markBusinessSeen] = useBusinessSeen(draftId);
    const businessSeen = purpose !== "first_check" || seenStored;
    const reach: StepReach = useMemo(
        () => ({ kind, purpose, hasDraftId: Boolean(draftId), draft, hasForm: Boolean(form || site.detected), businessSeen }),
        [kind, purpose, draftId, draft, form, site.detected, businessSeen],
    );
    const pending = Boolean(draftId && !draft) || (routeKind === "business" && !draftId && !openError);
    const canReach = useCallback((s: StepKey) => canReachStep(s, reach), [reach]);
    const step: StepKey = isStepKey(askedStep) && canReachStep(askedStep, reach) ? askedStep : defaultStep(reach);

    // Name the step in the URL when it names none (or nonsense). A valid step
    // that cannot be reached yet stays in the URL: a write that has just made it
    // reachable (Approve, then Start) may land a render after the move.
    useEffect(() => {
        if (pending || isStepKey(askedStep)) return;
        setParams(
            (was) => {
                const next = new URLSearchParams(was);
                next.set("step", step);
                return next;
            },
            { replace: true },
        );
    }, [pending, askedStep, step, setParams]);

    const clearMessage = ctl.clearMessage;
    const goTo = useCallback(
        (s: StepKey) => {
            // Not gated on reach: the step renders once its write lands (see above).
            if (!stepsFor(kind, purpose).includes(s)) return;
            // The last write's line belongs to the step it was made on.
            clearMessage();
            setParams(
                (was) => {
                    const next = new URLSearchParams(was);
                    next.set("step", s);
                    return next;
                },
                { replace: true },
            );
        },
        [kind, purpose, setParams, clearMessage],
    );

    const close = useCallback(
        (opts?: { notice?: string }) => {
            // Back to the screen the flow was opened from; without one, the list
            // for a new business, the business's own page for its draft.
            // A First AI check always goes back to onboarding, where its Next card lives.
            const to =
                firstCheck && effectiveBusinessId
                    ? onboardingHref(effectiveBusinessId)
                    : origin ??
                (kind !== "new_scan" && effectiveBusinessId
                    ? `${OURA_ROOT}/businesses/${encodeURIComponent(effectiveBusinessId)}`
                    : `${OURA_ROOT}/businesses`);
            // The page's own snackbar unmounts with it, so the notice rides along
            // in the navigation state for the screen it lands on. "Draft saved"
            // only when the draft still holds work (not started, not discarded),
            // and it says where to find the draft again.
            const saved = firstCheck ? FIRST_CHECK.closeSaved : kind === "new_scan" ? FRAME.closeSaved : FRAME.closeSavedBusiness;
            const notice = opts?.notice ?? (draftId && draft && OPEN_STATUSES.includes(draft.status) ? saved : null);
            navigate(to, notice ? { state: { scanDraftNotice: notice } } : undefined);
            // The flow's scroll is not the landing screen's: start at its top, where Resume and Scanning sit.
            window.scrollTo({ top: 0 });
        },
        [kind, firstCheck, effectiveBusinessId, draftId, draft, navigate, origin],
    );

    /* ---- the foot ---- */
    const footStore = useMemo(createFootStore, []);
    const foot = useMemo(() => ({ set: (next: FootSpec | null) => footStore.set(step, next) }), [footStore, step]);

    const value: ScanFlowValue = useMemo(
        () => ({
            kind,
            purpose,
            businessId: effectiveBusinessId ?? null,
            steps,
            step,
            goTo,
            canReach,
            businessSeen,
            markBusinessSeen,
            close,
            ctl,
            draftId,
            site,
            setDomain,
            readSite,
            form,
            setForm,
            gsc,
            connectGsc,
            recheckGsc,
            createDraft,
            creating,
            openDraft,
            startOver,
            foot,
        }),
        [
            kind,
            purpose,
            effectiveBusinessId,
            steps,
            step,
            goTo,
            canReach,
            businessSeen,
            markBusinessSeen,
            close,
            ctl,
            draftId,
            site,
            setDomain,
            readSite,
            form,
            setForm,
            gsc,
            connectGsc,
            recheckGsc,
            createDraft,
            creating,
            openDraft,
            startOver,
            foot,
        ],
    );

    return { value, footStore, pending, openError, retryOpen, notice };
}
